import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { isDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import Chip from '@zola/zola-ui/src/components/Chip';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import MEDIA_QUERY_V2 from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';

import styled from '@emotion/styled';
import _debounce from 'lodash/debounce';
import Sticky from 'react-stickynode';

import { AllFacetParentKey } from '~/types/types';

import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '../../contexts/VendorSearchNavigationContext';
import { isSortingFilter } from '../SearchControls/facets/FacetButton/FacetButton';
import { FacetSideDrawer } from '../SearchControls/facets/FacetSideDrawer/FacetSideDrawer';
import { useSearchCategories } from '../SearchControls/useSearchCategories';

import styles from './mobileSearchFilters.module.less';

const MobileStickyFilters = styled.div`
  width: 100%;
  background-color: ${COLORS3.WHITE_100};
  padding: 16px;
  position: relative;

  display: flex;
  gap: 8px;
  overflow-x: auto;

  ${MEDIA_QUERY_V2.DESKTOP} {
    display: none;
  }
`;

const MobileFilterChip = styled(Chip)`
  flex-shrink: 0;
`;

const SCRIM_WIDTH = 40;
const Scrim = styled.div`
  top: 0;
  right: 0;
  width: ${SCRIM_WIDTH}px;
  height: 100%;

  background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  position: absolute;
  pointer-events: none;
`;

// This should maybe be 60 for the 2px drop shadow?
const SMALL_NAV_HEIGHT = 58;

export const MobileSearchFilters = () => {
  const { vendorSearch, selectedFacetOptions } = useSearchResultsPageContext();

  const { navigateToSrp } = useVendorSearchNavigation();

  const [drawerOpenTo, setDrawerOpenTo] = useState<null | true | AllFacetParentKey>(null);

  const allFacetCategories = useSearchCategories(vendorSearch);

  const computeTopOffset = useCallback(() => {
    if (isDesktop()) {
      // This has to match the nav breakpoints. At 992px to 1023px we have a
      // secondary nav menu that adds 75px.
      setTopOffset(133);
    } else {
      setTopOffset(SMALL_NAV_HEIGHT);
    }
  }, []);

  const [topOffset, setTopOffset] = useState(SMALL_NAV_HEIGHT);
  useEffect(() => {
    computeTopOffset();
  }, [computeTopOffset]);

  const debouncedComputeTopOffset = useMemo(
    () => _debounce(computeTopOffset, 250),
    [computeTopOffset]
  );
  useEffect(() => {
    window.addEventListener('resize', debouncedComputeTopOffset);
    return () => {
      window.removeEventListener('resize', debouncedComputeTopOffset);
    };
  }, [debouncedComputeTopOffset]);

  const clearFilters = useCallback(() => {
    navigateToSrp({
      awardGroups: null,
      selectedFacets: null,
      price: null,
      capacity: null,
      searchTerm: null,
      availabilityDates: null,
      metroTypes: null,
      catererTypes: null,
      page: undefined,
    });
  }, [navigateToSrp]);

  const visibleFacetCategories = useMemo(() => {
    return allFacetCategories.filter(
      (category) =>
        !category.dependsOnKey ||
        selectedFacetOptions?.some(
          (selectedFacetOption) => selectedFacetOption.key === category.dependsOnKey
        )
    );
  }, [allFacetCategories, selectedFacetOptions]);

  return (
    <Fragment>
      <Sticky innerZ={20} top={topOffset}>
        <MobileStickyFilters>
          {visibleFacetCategories.map((category) => {
            const { displayName, facets } = category;

            const mappedFacets = (facets || []).map((childFacet) => ({
              ...childFacet,
              checked: isSortingFilter(category)
                ? childFacet.key === category.slug
                : selectedFacetOptions.some((facetOption) => facetOption.slug === childFacet.slug),
            }));

            const currentlySelectedFilters = mappedFacets.filter(({ checked }) => checked).length;

            const currentlySelectedFilterCount =
              selectedFacetOptions && !isSortingFilter(category) && currentlySelectedFilters > 0
                ? `(${currentlySelectedFilters})`
                : '';

            return (
              <MobileFilterChip
                key={category.key}
                noBorder
                active
                onClick={() => setDrawerOpenTo(category.key)}
              >
                {displayName} {currentlySelectedFilterCount}
              </MobileFilterChip>
            );
          })}
        </MobileStickyFilters>
        <Scrim />
      </Sticky>

      {/* This is in fixed position */}
      {Boolean(drawerOpenTo) && (
        <FacetSideDrawer
          drawerOpenTo={drawerOpenTo}
          onClose={() => setDrawerOpenTo(null)}
          categories={allFacetCategories}
          clearFilters={clearFilters}
          className={styles.facetSideDrawer}
        />
      )}
    </Fragment>
  );
};
